/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../_metronic/_partials/controls";
import { useSubheader } from "../../_metronic/layout";
import { toAbsoluteUrl } from "../../_metronic/_helpers";
import * as crud from "../application/Admin/_redux/users/usersCrud";

export function UserProfilePage() {
  const navigate = useNavigate();
  const subheader = useSubheader();
  const { user } = useSelector((state) => state.auth);
  const userobject = user?.userData || user || {};
  const userid = userobject.userid;
  const username = userobject.name || "";
  const useremail = userobject.email || "";
  const admin_role = userobject.admin_role || "admin";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    subheader.setTitle("My Profile & Security");
  }, [subheader]);

  const handleEditProfile = () => {
    if (!userid) return;
    const editUrl = userobject?.is_admin ? `/admin/${userid}/edit` : `/user/${userid}/edit`;
    navigate(editUrl);
  };

  const backToDashboard = () => navigate("/dashboard");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!newPassword || newPassword.trim().length < 6) {
      setMessage({ type: "danger", text: "New password must be at least 6 characters long." });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: "danger", text: "New password and confirmation do not match." });
      return;
    }

    setLoading(true);
    try {
      const res = await crud.changeMyPassword(newPassword.trim());
      setMessage({
        type: "success",
        text: res?.data?.message || "Password has been successfully changed!"
      });
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setMessage({
        type: "danger",
        text: err?.response?.data?.message || err?.message || "Failed to update password. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      {/* Profile Overview Card */}
      <div className="col-xl-5 col-lg-6 mb-6">
        <Card className="card-stretch">
          <CardHeader title="My Profile">
            <CardHeaderToolbar>
              <button
                type="button"
                onClick={backToDashboard}
                className="btn btn-sm btn-light mr-2"
              >
                <i className="fa fa-arrow-left mr-1" />
                Back
              </button>
              <button
                type="button"
                onClick={handleEditProfile}
                className="btn btn-sm btn-primary"
              >
                Edit Info
              </button>
            </CardHeaderToolbar>
          </CardHeader>
          <CardBody className="pt-0">
            <div className="d-flex flex-wrap align-items-center mb-8">
              <div className="symbol symbol-60 symbol-lg-80 symbol-circle symbol-light-primary flex-shrink-0 mr-5">
                <span className="symbol-label font-size-h3 font-weight-bolder text-primary">
                  {username ? username.charAt(0).toUpperCase() : "U"}
                </span>
              </div>
              <div className="flex-grow-1">
                <h3 className="font-weight-bolder text-dark font-size-h4 mb-1">
                  {username || "User"}
                </h3>
                <span className="label label-lg label-light-primary label-inline font-weight-bold">
                  {admin_role}
                </span>
              </div>
            </div>

            <div className="separator separator-dashed my-6" />

            <div className="d-flex align-items-center p-4 bg-light-primary rounded mb-4">
              <span className="svg-icon svg-icon-2x svg-icon-primary mr-3">
                <SVG
                  src={toAbsoluteUrl("/media/svg/icons/Communication/Mail.svg")}
                />
              </span>
              <div className="d-flex flex-column">
                <span className="text-muted font-weight-bold font-size-xs text-uppercase">
                  Email Address
                </span>
                <span className="font-weight-bolder text-dark font-size-sm">
                  {useremail}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center p-4 bg-light rounded">
              <span className="svg-icon svg-icon-2x svg-icon-dark-50 mr-3">
                <SVG
                  src={toAbsoluteUrl("/media/svg/icons/General/Shield-check.svg")}
                />
              </span>
              <div className="d-flex flex-column">
                <span className="text-muted font-weight-bold font-size-xs text-uppercase">
                  Account Status
                </span>
                <span className="font-weight-bolder text-success font-size-sm">
                  Active & Verified
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Change Password Card */}
      <div className="col-xl-7 col-lg-6 mb-6">
        <Card className="card-stretch">
          <CardHeader title="Change My Password">
            <CardHeaderToolbar>
              <span className="label label-light-warning label-inline font-weight-bold">
                Security Settings
              </span>
            </CardHeaderToolbar>
          </CardHeader>
          <CardBody>
            {message.text && (
              <div
                className={`alert alert-${message.type} alert-custom fade show mb-6 py-3 px-4`}
                role="alert"
              >
                <div className="alert-icon">
                  <i
                    className={`fa ${
                      message.type === "success"
                        ? "fa-check-circle text-success"
                        : "fa-exclamation-triangle text-danger"
                    }`}
                  ></i>
                </div>
                <div className="alert-text font-weight-bold">
                  {message.text}
                </div>
              </div>
            )}

            <form onSubmit={handleChangePassword}>
              <div className="form-group mb-5">
                <label className="font-weight-bolder text-dark">
                  New Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    placeholder="Enter new password (min. 6 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                    </button>
                  </div>
                </div>
                <span className="form-text text-muted font-size-xs">
                  Password must be at least 6 characters long.
                </span>
              </div>

              <div className="form-group mb-6">
                <label className="font-weight-bolder text-dark">
                  Confirm New Password <span className="text-danger">*</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <div className="d-flex align-items-center">
                <button
                  type="submit"
                  className="btn btn-primary font-weight-bold px-6 py-3"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fa fa-circle-notch fa-spin mr-2"></i> Updating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-key mr-2"></i> Change Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

